import React, { useCallback, useRef } from 'react';
import { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import { Typography } from '@canvas-logic/clp-shared-ui';

import { Portal } from '@/ui-kit/components/Portal';
import { Backdrop } from '@/ui-kit/components/Backdrop/Backdrop';
import { ModalCloseButton } from '@/ui-kit/components/ModalCloseButton/ModalCloseButton';
import { useClickOutside } from '@/ui-kit/hooks';

import { ModalName, modalStore } from './ModalStore';

import styles from './Modal.module.css';

interface ModalProps {
  modalName: ModalName;
  children: ReactNode;
  closeOnBackdropClick?: boolean;
  title?: string;
  className?: string;
  fullContent?: boolean;
}

export const Modal = observer(function Modal({
  modalName,
  children,
  closeOnBackdropClick = true,
  title,
  className,
  fullContent = false,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    modalStore.closeModal();
  }, []);

  useClickOutside([modalRef], handleClose, closeOnBackdropClick);

  if (!modalStore.getIsOpened(modalName)) {
    return null;
  }

  return (
    <Portal>
      <Backdrop className={styles.backdrop}>
        <div className={styles.wrapper}>
          <div
            ref={modalRef}
            role="dialog"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleClose();
              }
            }}
            className={classNames(styles.modalContainer, { [styles.fullContent]: fullContent }, className)}
          >
            {!fullContent && (
              <div className={styles.header}>
                <Typography variant="heading4" component="h1" color="disabled">
                  {title}
                </Typography>
                <ModalCloseButton onClick={handleClose} className={styles.closeButton} />
              </div>
            )}
            <div className={classNames(styles.content, { [styles.contentFull]: fullContent })}>{children}</div>
          </div>
        </div>
      </Backdrop>
    </Portal>
  );
});
